/**
 * Maps technical/raw errors to user-friendly messages for display in UI and toasts.
 */
export function getUserFriendlyErrorMessage(error: unknown): string {
  if (error == null) return 'An unexpected error occurred. Please try again.';

  const message =
    typeof error === 'string'
      ? error
      : error instanceof Error
        ? error.message
        : String(error);

  // Common runtime/undefined access errors
  if (
    message.includes("Cannot read properties of undefined") ||
    message.includes("Cannot read property of undefined") ||
    message.includes("reading 'toLowerCase'")
  ) {
    return 'Some document data is missing or invalid. Try refreshing the page.';
  }
  if (message.includes("Cannot read properties of null") || message.includes("reading '")) {
    return 'Something went wrong while loading. Please refresh the page.';
  }

  // Network / API
  if (
    message.includes('Failed to fetch') ||
    message.includes('NetworkError') ||
    message.includes('Network request failed')
  ) {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    if (apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1')) {
      return 'Cannot reach the local API. Start it from the backend folder: .\\scripts\\run-offline.ps1';
    }
    return 'Unable to connect. Check your internet connection and try again.';
  }
  if (message.includes('404') || message.includes('Not Found') || message.includes('NoSuchKey')) {
    return 'The requested item was not found.';
  }
  if (message.includes('403') || message.includes('Forbidden')) {
    return 'You don’t have permission to perform this action.';
  }
  if (message.includes('401') || message.includes('Unauthorized')) {
    return 'Please sign in again.';
  }
  if (message.includes('500') || message.includes('Internal Server Error')) {
    return 'Something went wrong on our side. Please try again later.';
  }
  if (message.includes('timeout') || message.includes('Timeout')) {
    return 'The request took too long. Please try again.';
  }

  const trimmed = message.trim();

  // Structured upload failure (reason + cleanup + next step) from apiService.uploadDocument
  if (trimmed.includes('Upload could not finish.') && trimmed.includes('Cleanup:')) {
    return trimmed;
  }

  // Keep short, readable API messages; avoid long stack traces or technical jargon
  if (trimmed.length > 120) {
    return 'Something went wrong. Please try again.';
  }
  if (/^[A-Z][a-z].*[.!?]$/.test(trimmed) || trimmed.length < 100) {
    return trimmed;
  }
  return trimmed || 'An unexpected error occurred. Please try again.';
}
